import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { guardarArchivo } from 'src/app/utils/descargar';

/**
 * CONSULTAS · CUENTAS: el libro de las cuentas de origen (CTA-01 a 07; Mario, 09-oct: «tener el control de las cuentas
 * desde donde se paga, todo lo que ha salido de esas cuentas y su historia; es importante para cruzar»).
 *   · La lista: cada cuenta (también las dadas de baja) con lo que salió (solo lo que el banco confirmó), lo que está en
 *     el banco sin respuesta, lo armado sin enviar y lo no procesado o anulado; nada de eso se suma (CTA-02).
 *   · ?cuenta=CODE abre su libro: por lote, cada uno con sus pagos; la historia de la cuenta; el Excel para cruzar.
 * Las reglas viven en el núcleo (modules/liquidacion/dominio/cuentas-libro.js).
 */
@Component({
  selector: 'app-liquidacion-cuentas',
  templateUrl: './liquidacion-cuentas.component.html'
})
export class LiquidacionCuentasComponent implements OnInit {
  f = { desde: '', hasta: '' };
  cuenta: string | null = null;
  r: any = null;          // el resumen de todas las cuentas
  libro: any = null;      // el libro de la cuenta abierta
  abierto: string | null = null;
  cargando = false;
  error = '';

  constructor(private ruta: ActivatedRoute, private router: Router, private liq: LiquidacionService) { }

  ngOnInit(): void {
    this.atajo('mes', false);
    this.ruta.queryParamMap.subscribe((q) => {
      this.cuenta = q.get('cuenta');
      this.abierto = null;
      this.buscar();
    });
  }

  private hoy(): string { return new Date(Date.now() - 4 * 3600e3).toISOString().slice(0, 10); }
  atajo = (k: 'mes' | 'ant', buscar = true): void => {
    const h = this.hoy();
    if (k === 'mes') { this.f = { desde: `${h.slice(0, 7)}-01`, hasta: h }; }
    if (k === 'ant') { const d = new Date(`${h.slice(0, 7)}-01T12:00:00Z`); d.setUTCDate(0); const fin = d.toISOString().slice(0, 10); this.f = { desde: `${fin.slice(0, 7)}-01`, hasta: fin }; }
    if (buscar) this.buscar();
  };
  esAtajo = (k: 'mes' | 'ant'): boolean => { const g = { ...this.f }; this.atajo(k, false); const ok = g.desde === this.f.desde && g.hasta === this.f.hasta; this.f = g; return ok; };

  buscar = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try {
      if (this.cuenta) this.libro = await this.liq.libroCuenta(this.cuenta, this.f);
      else this.r = await this.liq.libroCuentas(this.f);
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo leer el libro de las cuentas.'; }
    finally { this.cargando = false; }
  };

  abrirCuenta = (code: string | null): void => { this.router.navigate([], { queryParams: { vista: 'cuentas', cuenta: code }, queryParamsHandling: 'merge' }); };
  abrirLote = (code: string): void => { this.abierto = this.abierto === code ? null : code; };

  excel = async (): Promise<void> => {
    if (!this.cuenta) return;
    try {
      const blob = await this.liq.excelLibroCuenta(this.cuenta, this.f);
      guardarArchivo(blob, `libro-${this.cuenta.toLowerCase()}-${this.f.desde}-a-${this.f.hasta}.xlsx`);
    } catch (e: any) { this.error = 'No se pudo descargar el Excel del libro.'; }
  };

  fecha = (iso: string | null): string => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
  hora = (f: any): string => {
    if (!f) return '—';
    const d = new Date(new Date(f).getTime() - 4 * 3600e3);
    return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  };
  ultimos4 = (n: string): string => (n ? `····${String(n).slice(-4)}` : '');
  readonly ESTADO_LOTE: any = {
    BORRADOR: { t: 'Armado', c: 'kb-badge-contorno' }, GENERADO: { t: 'Generado', c: 'kb-badge-contorno' }, DESCARGADO: { t: 'Descargado', c: 'kb-badge-contorno' },
    ENVIADO: { t: 'En el banco', c: 'kb-badge-alerta' }, RESPUESTA_PARCIAL: { t: 'Respuesta parcial', c: 'kb-badge-alerta' }, CERRADO: { t: 'Cerrado', c: 'kb-badge-exito' },
    ANULADO: { t: 'Anulado', c: 'kb-badge-neutro' },
  };
  readonly ESTADO_PAGO: any = {
    PAGADO: { t: 'Pagado', c: 'kb-badge-exito' }, PENDIENTE: { t: 'Sin respuesta', c: 'kb-badge-alerta' }, NO_PROCESADO: { t: 'No procesado', c: 'kb-badge-peligro' },
    ANULADO: { t: 'Anulado', c: 'kb-badge-neutro' },
  };
  campo = (k: string): string => ({ code: 'Código', bank_name: 'Banco', account_number: 'Número', account_type: 'Tipo', holder_name: 'Titular', tax_id: 'RIF',
    currency_code: 'Moneda', methods: 'Métodos', status: 'Activa', notes: 'Notas', bdv: 'BDV' } as any)[k] || k;
  /** Un valor de la historia en texto: listas separadas por coma, la negociación de BDV por su número, sí/no. */
  valor = (v: any): string => {
    if (v === undefined || v === null || v === '') return '—';
    if (Array.isArray(v)) return v.join(', ') || '—';
    if (typeof v === 'boolean') return v ? 'sí' : 'no';
    if (typeof v === 'object') return v.negotiation_number ? `negociación ${v.negotiation_number}` : (Object.values(v).filter(Boolean).join(', ') || '—');
    return String(v);
  };
  cambios = (h: any): string[] => {
    if (!h.antes) return ['Alta'];
    const ks = Object.keys(h.despues || {}).filter((k) => JSON.stringify((h.antes || {})[k]) !== JSON.stringify(h.despues[k]) && !['provider', 'bank'].includes(k));
    return ks.length ? ks.map((k) => `${this.campo(k)}: ${this.valor((h.antes || {})[k])} → ${this.valor(h.despues[k])}`) : ['Guardada sin cambios'];
  };
}
